import React, { useState } from 'react';
import PropTypes from 'prop-types';
import css from './YfDataStatus.css';
import { Button, Spinner, Toggle } from '../../../components/widgets';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../../components/foundations/Icon';
import useYfAuditStatus from '../../hooks/useYfAuditStatus';
import useElapsed from '../../hooks/useElapsed';
import ConnectorRow from './ConnectorRow';
import { downloadAuditCsv } from '../../yfDataStatusCsv';
import {
    AUDIT_METRICS,
    formatAuditDay,
    formatElapsed,
    groupRows,
    ROW_GROUP,
    yfTranslationKey,
} from '../../yfDataStatusUtils';

const TOGGLE_LABEL_STYLE = { fontSize: 12, color: 'var(--gray700)', marginRight: '8px', whiteSpace: 'nowrap' };

// The first group is the headline's; DISABLED is the drawer below the table.
const TITLED_GROUPS = [ROW_GROUP.UNCHECKED, ROW_GROUP.MATCHED];

const _plural = (count, key) => yfTranslationKey(`${key}${count === 1 ? '_ONE' : '_MANY'}`);

function _headline(counts, headlineGroup, translate) {
    if (!headlineGroup) {
        return translate(yfTranslationKey('HEADLINE.ALL_MATCHED'));
    }
    return translate(_plural(counts[headlineGroup], `HEADLINE.${headlineGroup.toUpperCase()}`), {
        count: counts[headlineGroup],
    });
}

function _subHeadline(counts, headlineGroup, translate) {
    return [ROW_GROUP.MISSING, ROW_GROUP.GAP, ROW_GROUP.UNCHECKED, ROW_GROUP.MATCHED]
        .filter(group => group !== headlineGroup && counts[group])
        .map(group =>
            translate(_plural(counts[group], `SUB_HEADLINE.${group.toUpperCase()}`), {
                count: counts[group],
            })
        )
        .join(' ');
}

// Its own component so the per-second clock does not re-render the table.
function BulkProgress({ bulk, onStop, translate }) {
    const elapsedMs = useElapsed(bulk.startedAt);

    return (
        <div className={css.progress}>
            <div className={css.progressTrack}>
                <div className={css.progressFill} style={{ width: `${(bulk.done / bulk.total) * 100}%` }} />
            </div>
            <span className={css.progressLabel}>
                {translate(yfTranslationKey('PROGRESS'), { done: bulk.done, total: bulk.total })}
                <span className={css.progressElapsed}>{formatElapsed(elapsedMs)}</span>
            </span>
            <button type="button" className={css.linkButton} onClick={onStop}>
                {translate(yfTranslationKey('STOP_BUTTON'))}
            </button>
        </div>
    );
}

BulkProgress.propTypes = {
    bulk: PropTypes.shape({
        total: PropTypes.number.isRequired,
        done: PropTypes.number.isRequired,
        startedAt: PropTypes.number.isRequired,
    }).isRequired,
    onStop: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

function UnauditableDrawer({ rows, translate }) {
    const [open, setOpen] = useState(false);

    return (
        <div className={css.drawer}>
            <button type="button" className={css.drawerHeader} onClick={() => setOpen(!open)} aria-expanded={open}>
                <Icon
                    name={open ? 'chevron-up' : 'chevron-down'}
                    type={IconTypes.TABLER}
                    size={IconSizes.X_SMALL}
                    variant={IconVariants.LIGHT}
                    interactive={false}
                />
                <span className={css.drawerText}>
                    <span className={css.drawerTitle}>
                        {translate(yfTranslationKey('UNAUDITABLE_TITLE'), { count: rows.length })}
                    </span>
                    <span className={css.drawerHint}>{translate(yfTranslationKey('UNAUDITABLE_HINT'))}</span>
                </span>
            </button>
            {open && (
                <div className={css.drawerRows}>
                    {rows.map(connector => (
                        <div key={connector.uan_id} className={css.drawerRow}>
                            <span className={css.drawerName}>{connector.display_name}</span>
                            <span className={css.drawerLabel}>{connector.label}</span>
                            {connector.unsupported_reason && (
                                <span className={css.drawerReason}>
                                    {translate(
                                        yfTranslationKey(
                                            `UNAUDITABLE_REASONS.${connector.unsupported_reason.toUpperCase()}`
                                        )
                                    )}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

UnauditableDrawer.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    translate: PropTypes.func.isRequired,
};

function YfDataStatus({ translate = txt => txt }) {
    const {
        snapshot,
        visibleRows,
        archivedCount,
        calculableCount,
        loadError,
        calculationErrors,
        isLoading,
        startedAt,
        queuedUanIds,
        bulk,
        isCalculatingAny,
        showArchived,
        setShowArchived,
        calculate,
        calculateAll,
        stopCalculateAll,
    } = useYfAuditStatus({ translate });

    if (isLoading) {
        return <Spinner show expanded />;
    }

    if (!snapshot) {
        return <div className={css.error}>{loadError || translate(yfTranslationKey('LOAD_ERROR'))}</div>;
    }

    const groups = groupRows(visibleRows);
    const counts = Object.keys(groups).reduce((totals, group) => ({ ...totals, [group]: groups[group].length }), {});
    const headlineGroup = [ROW_GROUP.UNAVAILABLE, ROW_GROUP.MISSING, ROW_GROUP.GAP, ROW_GROUP.UNCHECKED].find(
        group => counts[group]
    );
    const unauditable = groups[ROW_GROUP.DISABLED] || [];

    const renderRow = connector => (
        <ConnectorRow
            key={connector.uan_id}
            connector={connector}
            isCalculating={connector.uan_id in startedAt}
            isQueued={queuedUanIds.includes(connector.uan_id)}
            startedAt={startedAt[connector.uan_id] || null}
            error={calculationErrors[connector.uan_id]}
            onCalculate={calculate}
            translate={translate}
        />
    );

    return (
        <div className={css.page}>
            <div className={css.header}>
                <div className={css.headerText}>
                    <div className={css.day}>{formatAuditDay(snapshot.audit_date)}</div>
                    <div className={css.orgSchedule}>
                        {translate(yfTranslationKey('ORG_SCHEDULE'), {
                            timezone: snapshot.org_timezone,
                            expectedDataHour: snapshot.expected_data_hour,
                        })}
                    </div>
                    <div className={css.headline}>{_headline(counts, headlineGroup, translate)}</div>
                    <div className={css.subHeadline}>
                        {_subHeadline(counts, headlineGroup, translate)}
                    </div>
                </div>
                <div className={css.headerActions}>
                    <Button
                        type="secondary"
                        onClick={() => downloadAuditCsv(visibleRows, snapshot.audit_date)}
                        disabled={!visibleRows.length}
                    >
                        {translate(yfTranslationKey('EXPORT_CSV_BUTTON'))}
                    </Button>
                    <Button type="primary" onClick={calculateAll} disabled={isCalculatingAny || !calculableCount}>
                        {translate(yfTranslationKey('CALCULATE_ALL_BUTTON'))}
                    </Button>
                </div>
            </div>

            {bulk && <BulkProgress bulk={bulk} onStop={stopCalculateAll} translate={translate} />}

            {loadError && <div className={css.inlineError}>{loadError}</div>}

            <div className={css.toolbar}>
                <Toggle
                    label="STATIC.PAGES.DATA_SOURCES.TOGGLE_ARCHIVED_UANS"
                    labelPosition="start"
                    checked={showArchived}
                    onToggle={() => setShowArchived(!showArchived)}
                    disabled={isCalculatingAny || !archivedCount}
                    ariaLabel={translate('STATIC.PAGES.DATA_SOURCES.TOGGLE_ARCHIVED_UANS')}
                    styleOverride={{ labelStyle: TOGGLE_LABEL_STYLE }}
                />
            </div>

            <div className={css.tableScroll}>
                <div className={css.table}>
                    <div className={css.tableHeader}>
                        <div>{translate(yfTranslationKey('CONNECTOR_COLUMN'))}</div>
                        {AUDIT_METRICS.map(metric => (
                            <div key={metric} className={css.metricHeader}>
                                {translate(yfTranslationKey(`METRICS.${metric.toUpperCase()}`))}
                            </div>
                        ))}
                        <div className={css.loadHeader}>{translate(yfTranslationKey('LAST_LOAD_COLUMN'))}</div>
                        <div className={css.checkHeader}>{translate(yfTranslationKey('LAST_CHECK_COLUMN'))}</div>
                    </div>

                    {(groups[ROW_GROUP.UNAVAILABLE] || []).map(renderRow)}
                    {(groups[ROW_GROUP.MISSING] || []).map(renderRow)}
                    {(groups[ROW_GROUP.GAP] || []).map(renderRow)}

                    {TITLED_GROUPS.filter(group => counts[group]).map(group => (
                        <React.Fragment key={group}>
                            <div className={`${css.sectionHeader} ${css[`section_${group}`] || ''}`}>
                                {translate(yfTranslationKey(`SECTIONS.${group.toUpperCase()}`), {
                                    count: counts[group],
                                })}
                            </div>
                            {groups[group].map(renderRow)}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {!visibleRows.length && <div className={css.empty}>{translate(yfTranslationKey('EMPTY'))}</div>}

            {!!unauditable.length && <UnauditableDrawer rows={unauditable} translate={translate} />}

            {!showArchived && !!archivedCount && (
                <div className={css.footnote}>
                    {translate(yfTranslationKey('ARCHIVED_FOOTNOTE'), { count: archivedCount })}
                </div>
            )}
        </div>
    );
}

YfDataStatus.propTypes = {
    translate: PropTypes.func,
};

export default YfDataStatus;
